import type { AccessDescriptor, CommercialAccessReason } from '../models/AccessDescriptor';
import type { CapabilityType, Entitlements } from '../models/Entitlements';
import type { ProductId } from '../models/ProductGrant';
import {
  getCommercialCapabilityPolicy,
  planMeetsMinimum,
} from './CommercialCapabilityRegistry';

export class CommercialAccessError extends Error {
  readonly status = 403;

  constructor(readonly descriptor: AccessDescriptor) {
    super(messageFor(descriptor.reasonCode, descriptor.requiredProduct));
  }

  get code(): CommercialAccessReason {
    return this.descriptor.reasonCode ?? 'PLAN_UPGRADE_REQUIRED';
  }
}

export class ProductAccessService {
  describe(input: {
    readonly entitlements: Entitlements;
    readonly capability: CapabilityType;
    readonly productContext?: ProductId;
  }): AccessDescriptor {
    const policy = getCommercialCapabilityPolicy(input.capability, input.productContext);
    if (!policy) throw new Error('COMMERCIAL_CAPABILITY_POLICY_UNKNOWN');

    const allowedByEffectiveCapabilities = input.entitlements.capabilities.capabilities
      .includes(input.capability);
    if (!input.entitlements.commercial) {
      return {
        allowed: allowedByEffectiveCapabilities,
        visible: allowedByEffectiveCapabilities,
        capability: input.capability,
        previewKind: 'none',
        currentPlan: input.entitlements.planType,
        ...(allowedByEffectiveCapabilities ? {} : {
          reasonCode: 'PLAN_UPGRADE_REQUIRED' as const,
          requiredPlan: policy.depth === 'advanced' ? 'enterprise' as const : 'pro' as const,
        }),
      };
    }

    if (policy.depth === 'basic') {
      const allowed = planMeetsMinimum(input.entitlements.planType, policy.minimumPlan)
        && allowedByEffectiveCapabilities;
      return {
        allowed,
        visible: true,
        capability: input.capability,
        previewKind: 'none',
        currentPlan: input.entitlements.planType,
        assistedOutcomeRemaining:
          input.entitlements.commercial.limits.assistedOutcomes.remaining,
        ...(allowed ? {} : {
          requiredPlan: 'pro' as const,
          reasonCode: 'PLAN_UPGRADE_REQUIRED' as const,
          upgradeRoute: 'pro_checkout' as const,
        }),
      };
    }

    const product = input.entitlements.commercial.products[policy.productId];
    // An active product evaluation carries temporary, product-scoped Enterprise
    // authority without changing the organization's baseline plan or limits.
    const allowed = product.advanced === 'allowed'
      && allowedByEffectiveCapabilities;
    const evaluationExpired = product.grantBasis === 'evaluation'
      && product.grantStatus === 'expired';
    return {
      allowed,
      visible: true,
      capability: input.capability,
      previewKind: allowed ? 'none' : policy.previewKind,
      ...(allowed || !policy.previewId ? {} : { previewId: policy.previewId }),
      currentPlan: input.entitlements.planType,
      requiredPlan: allowed ? undefined : 'enterprise',
      requiredProduct: policy.productId,
      reasonCode: allowed
        ? undefined
        : evaluationExpired
          ? 'ENTERPRISE_EVALUATION_EXPIRED'
          : 'ENTERPRISE_PRODUCT_GRANT_REQUIRED',
      upgradeRoute: allowed ? undefined : 'enterprise_sales',
      evaluationState: product.grantBasis === 'contract'
        ? 'contracted'
        : product.grantStatus ?? 'none',
    };
  }

  requireCapability(input: {
    readonly entitlements: Entitlements;
    readonly capability: CapabilityType;
    readonly productContext?: ProductId;
  }): AccessDescriptor {
    const descriptor = this.describe(input);
    if (!descriptor.allowed) throw new CommercialAccessError(descriptor);
    return descriptor;
  }
}

function messageFor(reason: CommercialAccessReason | undefined, productId?: ProductId): string {
  if (reason === 'ENTERPRISE_EVALUATION_EXPIRED') {
    return `The Enterprise ${title(productId)} evaluation has expired.`;
  }
  if (reason === 'ENTERPRISE_PRODUCT_GRANT_REQUIRED') {
    return `Enterprise ${title(productId)} access is required for this action.`;
  }
  return 'A plan upgrade is required for this action.';
}

function title(productId?: ProductId): string {
  if (!productId) return 'product';
  return productId.charAt(0).toUpperCase() + productId.slice(1);
}
